import { useState, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import { theme } from '../theme';

const btnBase = {
  padding: '6px 16px',
  borderRadius: '6px',
  fontSize: '0.85rem',
  fontWeight: '600',
  cursor: 'pointer',
  border: `1px solid ${theme.colors.border}`,
  backgroundColor: 'transparent',
  color: theme.colors.primary,
  transition: 'opacity 0.15s',
};

const DataTable = forwardRef(function DataTable({ columns, fetchUrl, token, renderActions, params }, ref) {
  const [data, setData] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Serializamos los filtros para tener una dependencia estable (evita loops de fetch)
  const paramsKey = JSON.stringify(params || {});

  const fetchData = useCallback(async (p) => {
    setLoading(true);
    setError('');
    try {
      const query = new URLSearchParams({ page: String(p) });
      const extra = JSON.parse(paramsKey);
      for (const [k, v] of Object.entries(extra)) {
        if (v != null && String(v).trim() !== '') query.set(k, String(v));
      }
      const res = await fetch(`${fetchUrl}?${query.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        setError(d.message || 'Error al cargar datos');
        return;
      }
      const d = await res.json();
      setData(d.data || []);
      setTotal(d.total || 0);
      setTotalPages(d.totalPages || 1);
    } catch {
      setError('Error de conexión');
    } finally {
      setLoading(false);
    }
  }, [fetchUrl, token, paramsKey]);

  // Al cambiar los filtros, volvemos a la primera página
  useEffect(() => {
    setPage(1);
  }, [paramsKey]);

  useEffect(() => {
    fetchData(page);
  }, [page, fetchData]);

  useImperativeHandle(ref, () => ({
    reload: () => {
      setPage(1);
      fetchData(1);
    },
  }));

  const colSpan = columns.length + (renderActions ? 1 : 0);

  return (
    <div>
      <div style={{ overflowX: 'auto', borderRadius: '10px', border: `1px solid ${theme.colors.border}` }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
          <thead>
            <tr style={{ backgroundColor: theme.colors.background }}>
              {columns.map((col) => (
                <th key={col.key} style={{
                  padding: '12px 16px',
                  textAlign: 'left',
                  color: theme.colors.primary,
                  fontWeight: '600',
                  borderBottom: `1px solid ${theme.colors.border}`,
                  whiteSpace: 'nowrap',
                }}>
                  {col.label}
                </th>
              ))}
              {renderActions && (
                <th style={{
                  padding: '12px 16px',
                  textAlign: 'center',
                  color: theme.colors.primary,
                  fontWeight: '600',
                  borderBottom: `1px solid ${theme.colors.border}`,
                  whiteSpace: 'nowrap',
                }}>
                  Acciones
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={colSpan} style={{ padding: '2.5rem', textAlign: 'center', color: theme.colors.textMuted }}>
                  Cargando...
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan={colSpan} style={{ padding: '2rem', textAlign: 'center', color: theme.colors.error }}>
                  {error}
                </td>
              </tr>
            ) : data.length === 0 ? (
              <tr>
                <td colSpan={colSpan} style={{ padding: '2.5rem', textAlign: 'center', color: theme.colors.textMuted }}>
                  No hay datos disponibles
                </td>
              </tr>
            ) : (
              data.map((row, i) => (
                <tr
                  key={row.id ?? i}
                  style={{
                    borderBottom: `1px solid ${theme.colors.border}`,
                    backgroundColor: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.02)',
                  }}
                >
                  {columns.map((col) => (
                    <td key={col.key} style={{ padding: '11px 16px', color: theme.colors.text, verticalAlign: 'middle' }}>
                      {col.render ? col.render(row) : (row[col.key] ?? '—')}
                    </td>
                  ))}
                  {renderActions && (
                    <td style={{ padding: '8px 16px', textAlign: 'center', verticalAlign: 'middle' }}>
                      {renderActions(row)}
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '1rem', flexWrap: 'wrap', gap: '0.75rem' }}>
        <span style={{ color: theme.colors.textMuted, fontSize: '0.85rem' }}>
          {total} registro{total !== 1 ? 's' : ''} en total
        </span>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center', maxWidth: '100%' }}>
          <button
            disabled={page <= 1 || loading}
            onClick={() => setPage((p) => p - 1)}
            style={{ ...btnBase, opacity: page <= 1 || loading ? 0.4 : 1, cursor: page <= 1 || loading ? 'not-allowed' : 'pointer' }}
          >
            ← Anterior
          </button>
          <span style={{ color: theme.colors.text, fontSize: '0.9rem', minWidth: '90px', textAlign: 'center' }}>
            Página {page} de {totalPages}
          </span>
          <button
            disabled={page >= totalPages || loading}
            onClick={() => setPage((p) => p + 1)}
            style={{ ...btnBase, opacity: page >= totalPages || loading ? 0.4 : 1, cursor: page >= totalPages || loading ? 'not-allowed' : 'pointer' }}
          >
            Siguiente →
          </button>
        </div>
      </div>
    </div>
  );
});

export default DataTable;
